import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { getRitual, SANDHYA_STEPS, SANDHYA_TIMES, GAYATRI } from "@/data/rituals";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";

const TradTags = ({ list }) => (
  <div className="mt-3 flex flex-wrap gap-1.5">
    {list.map((t) => (
      <span key={t} className="rounded-full border border-[#A8722A]/40 px-2.5 py-0.5 font-mukta text-[11px] text-[#A8722A]">{t}</span>
    ))}
  </div>
);

const StepRow = ({ i, step, extra }) => (
  <li className="flex flex-col gap-4 rounded-[16px] border border-[#A8722A]/20 bg-[#FFFDF8] p-4 sm:flex-row sm:items-center" data-testid={`ritual-step-${i}`}>
    {step.img && (
      <div className="flex h-28 w-28 shrink-0 items-center justify-center self-center rounded-[12px] bg-[#F3EADA]">
        <img src={step.img} alt={step.en} loading="lazy" className="h-full w-full object-contain" />
      </div>
    )}
    <div className="flex-1">
      <div className="flex items-baseline gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#B23A2B] text-[12px] font-bold text-white">{i + 1}</span>
        <p className="font-baloo text-base text-[#B23A2B]">{step.kn}</p>
      </div>
      <h3 className="mt-0.5 font-eczar text-lg font-semibold">{step.en}</h3>
      <p className="mt-1 font-mukta text-[15px] text-[#3B221B]">{step.do}</p>
      {step.purpose && <p className="mt-1 font-mukta text-sm text-[#6B5044]"><em>Why:</em> {step.purpose}</p>}
      {extra}
    </div>
  </li>
);

export default function RitualPage() {
  const { slug } = useParams();
  const [time, setTime] = useState("pratah");
  const r = getRitual(slug);
  const t = SANDHYA_TIMES.find((x) => x.key === time);

  useSeo({
    title: r ? `${r.title.en} — ${r.title.kn} | Step-by-step guide | PurohithConnect` : "Rituals & Vratas | PurohithConnect",
    description: r ? `${r.summary.en} A clear, learner-friendly step-by-step guide. Tradition: ${r.tradition.join(", ")}.` : "Step-by-step ritual and vrata guides.",
    canonicalPath: r ? `/rituals/${r.slug}` : "/rituals",
    jsonLd: r ? {
      "@context": "https://schema.org", "@type": "HowTo", name: `${r.title.en} — step by step`,
      description: r.summary.en,
      step: (r.type === "sandhya" ? SANDHYA_STEPS : r.steps || []).map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.en, text: s.do })),
    } : undefined,
  });

  if (!r) return <Navigate to="/rituals" replace />;

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]">
      <SiteHeader />
      <article className="mx-auto max-w-3xl px-4 py-10 md:px-8">
        <Link to="/rituals" className="inline-flex items-center gap-1 font-mukta text-sm text-[#6B5044] hover:text-[#3B221B]" data-testid="ritual-back">
          <ChevronLeft size={16} /> Rituals &amp; Vratas
        </Link>
        <p className="mt-4 font-baloo text-xl text-[#B23A2B]">{r.title.kn}</p>
        <h1 className="mt-1 font-eczar text-3xl font-bold sm:text-4xl" data-testid="ritual-title">{r.title.en}</h1>
        <p className="mt-3 font-mukta text-base text-[#6B5044]">{r.summary.en}</p>
        <TradTags list={r.tradition} />
        {r.shakha && <p className="mt-3 font-mukta text-sm text-[#6B5044]"><span className="font-semibold text-[#3B221B]">Shakha / sutra:</span> {r.shakha}</p>}
        {r.deity && <p className="mt-1 font-mukta text-sm text-[#6B5044]"><span className="font-semibold text-[#3B221B]">Deity:</span> {r.deity}</p>}
        {r.when && <p className="mt-1 font-mukta text-sm text-[#6B5044]"><span className="font-semibold text-[#3B221B]">When:</span> {r.when}</p>}

        {r.type === "sandhya" && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="sandhya-time-tabs">
            {SANDHYA_TIMES.map((x) => (
              <button key={x.key} onClick={() => setTime(x.key)} data-testid={`time-${x.key}`}
                className={`rounded-full border px-4 py-1.5 font-mukta text-sm font-medium transition-colors ${time === x.key ? "border-[#B23A2B] bg-[#B23A2B] text-white" : "border-[#A8722A]/30 text-[#6B5044] hover:border-[#A8722A]"}`}>
                {x.en}
              </button>
            ))}
          </div>
        )}

        {r.type === "sandhya" && t && (
          <div className="mt-4 rounded-[14px] border border-[#A8722A]/25 bg-[#F3EADA]/70 p-4 font-mukta text-sm text-[#3B221B]" data-testid="sandhya-time-note">
            <p className="font-baloo text-base text-[#B23A2B]">{t.kn}</p>
            <p className="mt-1"><span className="font-semibold">When:</span> {t.when}</p>
            <p className="mt-1"><span className="font-semibold">Arghya:</span> {t.arghya}</p>
            <p className="mt-1"><span className="font-semibold">Meditation:</span> {t.meditate}</p>
          </div>
        )}

        {r.materials && (
          <div className="mt-6">
            <h2 className="font-eczar text-xl font-semibold">Samagri you'll need</h2>
            <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {r.materials.map((m, i) => (
                <li key={i} className="rounded-lg border border-[#A8722A]/20 bg-[#FFFDF8] px-3 py-1.5 font-mukta text-sm text-[#6B5044]" data-testid={`ritual-material-${i}`}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        <h2 className="mt-8 font-eczar text-xl font-semibold">Step by step</h2>
        <ol className="mt-3 space-y-3">
          {(r.type === "sandhya" ? SANDHYA_STEPS : r.steps).map((s, i) => (
            <StepRow key={i} i={i} step={s}
              extra={r.type === "sandhya" && s.mantra ? (
                <div className="mt-2 rounded-lg border border-[#3E6B4F]/30 bg-[#3E6B4F]/[0.06] px-3 py-2">
                  <p className="font-baloo text-[15px] text-[#3B221B]">{GAYATRI.kn}</p>
                  <p className="mt-1 font-mukta text-xs italic text-[#6B5044]">{GAYATRI.en}</p>
                </div>
              ) : null}
            />
          ))}
        </ol>

        {r.note && (
          <div className="mt-6 rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 font-mukta text-sm text-[#6B5044]" data-testid="ritual-note">
            <span className="font-semibold text-[#3B221B]">Note:</span> {r.note}
          </div>
        )}

        <p className="mt-6 font-mukta text-xs text-[#6B5044]">
          This is a learner's outline. Exact mantras and small variations differ by shakha, sutra and family custom — please learn the full procedure from your guru or purohith.
        </p>
      </article>
      <SiteFooter />
    </div>
  );
}
